import { apiFetch, apiFetchBlob } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import type {
  CsedAuditRow,
  EntityDetail,
  ImpersonateResult,
  ImpersonationSession,
  JobRun,
  JobSpec,
  ListMeta,
  LoginEvent,
  OpsUser,
} from "./types";

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
  meta?: ListMeta;
}

// ---- snake → camel mappers ------------------------------------------------ //

interface ApiOpsUser {
  id: number;
  name: string;
  email: string;
  account_type: "firm" | "individual";
  role: "staff" | "team_leader" | "country_director" | null;
  firm: { id: string; name: string } | null;
  is_active: boolean;
  is_staff: boolean;
  is_platform_admin: boolean;
  last_login: string | null;
  date_joined: string | null;
}

function toUser(a: ApiOpsUser): OpsUser {
  return {
    id: a.id,
    name: a.name,
    email: a.email,
    accountType: a.account_type,
    role: a.role,
    firm: a.firm,
    isActive: a.is_active,
    isStaff: a.is_staff,
    isPlatformAdmin: a.is_platform_admin,
    lastLogin: a.last_login,
    dateJoined: a.date_joined,
  };
}

interface ApiSession {
  id: string;
  operator: ApiOpsUser;
  target: ApiOpsUser;
  started_at: string;
  ended_at: string | null;
  operator_ip: string | null;
}

function toSession(a: ApiSession): ImpersonationSession {
  return {
    id: a.id,
    operator: toUser(a.operator),
    target: toUser(a.target),
    startedAt: a.started_at,
    endedAt: a.ended_at,
    operatorIp: a.operator_ip,
  };
}

interface ApiJobRun {
  id: string;
  job_name: string;
  trigger: string;
  triggered_by_email: string | null;
  status: JobRun["status"];
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  records_processed: number;
  summary: string;
  error: string;
}

function toJobRun(a: ApiJobRun | null): JobRun | null {
  if (!a) return null;
  return {
    id: a.id,
    jobName: a.job_name,
    trigger: a.trigger,
    triggeredByEmail: a.triggered_by_email,
    status: a.status,
    startedAt: a.started_at,
    finishedAt: a.finished_at,
    durationMs: a.duration_ms,
    recordsProcessed: a.records_processed,
    summary: a.summary,
    error: a.error,
  };
}

// ---- users + impersonation ------------------------------------------------ //

export async function searchUsers(search: string): Promise<{ users: OpsUser[]; meta: ListMeta }> {
  const query = search ? `?search=${encodeURIComponent(search)}` : "";
  const body = await apiFetch<Envelope<ApiOpsUser[]>>(`/ops/users/${query}`);
  return {
    users: body.data.map(toUser),
    meta: body.meta ?? { count: body.data.length, next: null, previous: null },
  };
}

export async function impersonate(userId: number, password: string): Promise<ImpersonateResult> {
  const body = await apiFetch<
    Envelope<{
      access: string;
      refresh: string;
      user: ApiOpsUser;
      session: ApiSession;
    }>
  >("/ops/impersonate/", { method: "POST", json: { user_id: userId, password } });
  return {
    access: body.data.access,
    refresh: body.data.refresh,
    user: toUser(body.data.user),
    session: toSession(body.data.session),
  };
}

export async function stopImpersonation(): Promise<void> {
  await apiFetch("/ops/impersonate/stop/", { method: "POST", json: {} });
}

// ---- login log ------------------------------------------------------------ //

export async function getLoginLog(
  userId: number,
  params: { from?: string; to?: string } = {},
): Promise<{ events: LoginEvent[]; meta: ListMeta }> {
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  const query = qs.toString() ? `?${qs.toString()}` : "";
  const body = await apiFetch<
    Envelope<
      Array<{
        id: string;
        logged_at: string;
        login_type: string;
        ip_address: string | null;
        session_key: string;
      }>
    >
  >(`/ops/users/${userId}/login-log/${query}`);
  return {
    events: body.data.map((e) => ({
      id: e.id,
      loggedAt: e.logged_at,
      loginType: e.login_type,
      ipAddress: e.ip_address,
      sessionKey: e.session_key,
    })),
    meta: body.meta ?? { count: body.data.length, next: null, previous: null },
  };
}

export async function downloadLoginLog(
  userId: number,
  params: { from?: string; to?: string } = {},
): Promise<void> {
  const qs = new URLSearchParams({ format: "xlsx" });
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  const blob = await apiFetchBlob(`/ops/users/${userId}/login-log/?${qs.toString()}`);
  downloadBlob(`login-history-${userId}.xlsx`, blob);
}

// ---- reports (xlsx) ------------------------------------------------------- //

export async function downloadReport(
  kind: string,
  params: { from?: string; to?: string } = {},
): Promise<void> {
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  const query = qs.toString() ? `?${qs.toString()}` : "";
  const blob = await apiFetchBlob(`/ops/reports/${kind}/${query}`);
  downloadBlob(`${kind}.xlsx`, blob);
}

// ---- CSED audit ----------------------------------------------------------- //

export async function getCsedAudit(
  entityId: string,
): Promise<{ rows: CsedAuditRow[]; count: number }> {
  const body = await apiFetch<
    Envelope<{
      rows: Array<{
        client: string;
        tax_year: string;
        form: string;
        calculated_csed: string | null;
        irs_csed: string | null;
        primary_practitioner: string;
      }>;
      meta: { count: number };
    }>
  >(`/ops/csed-audit/?entity=${encodeURIComponent(entityId)}`);
  return {
    rows: body.data.rows.map((r) => ({
      client: r.client,
      taxYear: r.tax_year,
      form: r.form,
      calculatedCsed: r.calculated_csed,
      irsCsed: r.irs_csed,
      primaryPractitioner: r.primary_practitioner,
    })),
    count: body.data.meta?.count ?? body.data.rows.length,
  };
}

export async function downloadCsedAudit(entityId: string): Promise<void> {
  const blob = await apiFetchBlob(
    `/ops/csed-audit/?format=xlsx&entity=${encodeURIComponent(entityId)}`,
  );
  downloadBlob("csed-audit.xlsx", blob);
}

// ---- TDS health + bad A2A (seams) ----------------------------------------- //

export interface TdsHealth {
  date: string;
  hours: Array<{ hour: number; requests: number; unknowns: number; errors: number }>;
  alertActive: boolean;
  note?: string;
}

export async function getTdsHealth(date?: string): Promise<TdsHealth> {
  const query = date ? `?date=${date}` : "";
  const body = await apiFetch<
    Envelope<{
      date: string;
      hours: Array<{ hour: number; requests: number; unknowns: number; errors: number }>;
      alert_active: boolean;
      note?: string;
    }>
  >(`/ops/tds-health/${query}`);
  return {
    date: body.data.date,
    hours: body.data.hours,
    alertActive: body.data.alert_active,
    note: body.data.note,
  };
}

export interface BadA2A {
  rows: Array<Record<string, unknown>>;
  count: number;
  note?: string;
}

export async function getBadA2A(): Promise<BadA2A> {
  const body = await apiFetch<
    Envelope<{
      rows: Array<Record<string, unknown>>;
      meta: { count: number };
      note?: string;
    }>
  >("/ops/bad-a2a/");
  return { rows: body.data.rows, count: body.data.meta?.count ?? 0, note: body.data.note };
}

export async function clearA2ACounter(): Promise<void> {
  await apiFetch("/ops/bad-a2a/clear-counter/", { method: "POST", json: {} });
}

// ---- entities ------------------------------------------------------------- //

export async function getEntity(entityId: string): Promise<EntityDetail> {
  const body = await apiFetch<
    Envelope<{
      id: string;
      name: string;
      email: string;
      created_at: string;
      user_count: number;
      client_count: number;
      document_count: number;
      users: Array<{
        id: number;
        name: string;
        email: string;
        role: string | null;
        is_active: boolean;
        last_login: string | null;
      }>;
    }>
  >(`/ops/entities/${entityId}/`);
  const d = body.data;
  return {
    id: d.id,
    name: d.name,
    email: d.email,
    createdAt: d.created_at,
    userCount: d.user_count,
    clientCount: d.client_count,
    documentCount: d.document_count,
    users: d.users.map((u) => ({
      id: u.id,
      name: u.name,
      email: u.email,
      role: u.role,
      isActive: u.is_active,
      lastLogin: u.last_login,
    })),
  };
}

export async function deleteEntity(entityId: string, confirm: string): Promise<void> {
  await apiFetch(`/ops/entities/${entityId}/delete/`, { method: "POST", json: { confirm } });
}

export async function bulkDeleteEntities(entityList: string, confirm: string): Promise<void> {
  await apiFetch("/ops/entities/bulk-delete/", {
    method: "POST",
    json: { entity_list: entityList, confirm },
  });
}

// ---- jobs console --------------------------------------------------------- //

export async function listJobs(): Promise<JobSpec[]> {
  const body = await apiFetch<
    Envelope<
      Array<{
        name: string;
        label: string;
        description: string;
        cadence: string;
        manual: boolean;
        last_run: ApiJobRun | null;
      }>
    >
  >("/ops/jobs/");
  return body.data.map((j) => ({
    name: j.name,
    label: j.label,
    description: j.description,
    cadence: j.cadence,
    manual: j.manual,
    lastRun: toJobRun(j.last_run),
  }));
}

export async function runJob(name: string): Promise<void> {
  await apiFetch(`/ops/jobs/${name}/run/`, { method: "POST", json: {} });
}

export async function getJobRuns(name: string): Promise<{ runs: JobRun[]; meta: ListMeta }> {
  const body = await apiFetch<Envelope<ApiJobRun[]>>(`/ops/jobs/${name}/runs/`);
  return {
    runs: body.data.map((r) => toJobRun(r) as JobRun),
    meta: body.meta ?? { count: body.data.length, next: null, previous: null },
  };
}

// ---- Transcript Code Lab -------------------------------------------------- //

export interface CodeTallyRow {
  taxRecordId: string;
  firm: string;
  client: string;
  taxYear: number;
  form: string;
  date: string | null;
  amountCents: number;
  explanation: string;
}
export interface CodeTally {
  code: string | null;
  total: number;
  rows: CodeTallyRow[];
  topCodes: Array<{ code: string; count: number }>;
}

export async function tallyCode(code: string): Promise<CodeTally> {
  const q = code.trim() ? `?code=${encodeURIComponent(code.trim())}` : "";
  const body = await apiFetch<
    Envelope<{
      code: string | null;
      total?: number;
      rows?: Array<{
        tax_record_id: string;
        firm: string;
        client: string;
        tax_year: number;
        form: string;
        date: string | null;
        amount_cents: number;
        explanation: string;
      }>;
      top_codes?: Array<{ code: string; count: number }>;
    }>
  >(`/ops/codes/tally/${q}`);
  const d = body.data;
  return {
    code: d.code,
    total: d.total ?? 0,
    topCodes: d.top_codes ?? [],
    rows: (d.rows ?? []).map((r) => ({
      taxRecordId: r.tax_record_id,
      firm: r.firm,
      client: r.client,
      taxYear: r.tax_year,
      form: r.form,
      date: r.date,
      amountCents: r.amount_cents,
      explanation: r.explanation,
    })),
  };
}

export interface CodeSeqMatch {
  taxRecordId: string;
  firm: string;
  client: string;
  taxYear: number;
  form: string;
  startDate: string;
  endDate: string;
  gapDays: number;
}
export interface CodeSequence {
  start: string;
  end: string;
  days: number;
  matchCount: number;
  matches: CodeSeqMatch[];
}

export async function sequenceCodes(
  start: string,
  end: string,
  days: number,
): Promise<CodeSequence> {
  const body = await apiFetch<
    Envelope<{
      start: string;
      end: string;
      days: number;
      match_count: number;
      matches: Array<{
        tax_record_id: string;
        firm: string;
        client: string;
        tax_year: number;
        form: string;
        start_date: string;
        end_date: string;
        gap_days: number;
      }>;
    }>
  >(
    `/ops/codes/sequence/?start=${encodeURIComponent(start)}&end=${encodeURIComponent(
      end,
    )}&days=${days}`,
  );
  const d = body.data;
  return {
    start: d.start,
    end: d.end,
    days: d.days,
    matchCount: d.match_count,
    matches: d.matches.map((m) => ({
      taxRecordId: m.tax_record_id,
      firm: m.firm,
      client: m.client,
      taxYear: m.tax_year,
      form: m.form,
      startDate: m.start_date,
      endDate: m.end_date,
      gapDays: m.gap_days,
    })),
  };
}

export interface TranscriptItem {
  code: string;
  explanation: string;
  date: string | null;
  amountCents: number;
}
export interface TranscriptDetail {
  id: string;
  firm: string;
  client: string;
  taxYear: number;
  form: string;
  forWho: string;
  balanceCents: number;
  totalOwedCents: number;
  csedDate: string | null;
  transactions: TranscriptItem[];
}

export async function getTranscriptDetail(id: string): Promise<TranscriptDetail> {
  const body = await apiFetch<
    Envelope<{
      id: string;
      firm: string;
      client: string;
      tax_year: number;
      form: string;
      for_who: string;
      balance_cents: number;
      total_owed_cents: number;
      csed_date: string | null;
      transactions: Array<{ code: string; explanation: string; date: string | null; amount_cents: number }>;
    }>
  >(`/ops/transcripts/${id}/`);
  const d = body.data;
  return {
    id: d.id,
    firm: d.firm,
    client: d.client,
    taxYear: d.tax_year,
    form: d.form,
    forWho: d.for_who,
    balanceCents: d.balance_cents,
    totalOwedCents: d.total_owed_cents,
    csedDate: d.csed_date,
    transactions: d.transactions.map((t) => ({
      code: t.code,
      explanation: t.explanation,
      date: t.date,
      amountCents: t.amount_cents,
    })),
  };
}
